import { Link } from "react-router-dom";
import { ArrowRight, Target, Broadcast, ArrowsIn } from "@phosphor-icons/react";
import Layout, { PageHeader } from "../components/Layout";
import SEO from "../components/SEO";
import { Reveal, Stagger, StaggerItem } from "../components/motion";
import ClientLogos from "../components/ClientLogos";
import { IMAGES } from "../data/images";

const brands = [
  { name: "Eazymuv", role: "Parent / execution partner", d: "One accountable operating partner across factory, warehouse and logistics." },
  { name: "Eazymuv OS", role: "Technology ecosystem", d: "EazyOS — the Eazymuv Operating System connecting managed operations and EazyMS into one 100%-visibility view." },
  { name: "EazyMS", role: "Warehouse & factory product", d: "Inventory, labour and dispatch as one operating system." },
];

export default function About() {
  return (
    <Layout>
      <SEO title="About Eazymuv" description="Eazymuv is a technology-enabled supply chain execution company operating across three brand layers: Eazymuv, Eazymuv OS (EazyOS) and EazyMS." />
      <PageHeader eyebrow="About" title={<>An execution company, not a software vendor</>} subtitle="Eazymuv exists to close the execution gap — where plans meet the floor — with one accountable operating model and one technology ecosystem." image={IMAGES.forkliftYellow} imageAlt="Eazymuv operations in a cold storage warehouse" />

      <section className="bg-white border-b border-ez-text">
        <div className="max-w-[1600px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <Reveal>
            <div className="font-mono-ez text-xs uppercase tracking-[0.3em] text-ez-text/50 mb-6">Four brand layers</div>
            <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tighter max-w-3xl leading-tight">One company. One accountability. Four surfaces.</h2>
          </Reveal>
          <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-2 border border-ez-text">
            {brands.map((b, i) => (
              <StaggerItem key={i} className="border-b border-r border-ez-border p-8 md:p-10">
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-3xl uppercase tracking-tighter">{b.name}</span>
                  <span className="font-mono-ez text-xs uppercase tracking-wider text-ez-text/40">{b.role}</span>
                </div>
                <p className="mt-4 text-ez-text/70">{b.d}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <ClientLogos />

      <section className="bg-ez-bg border-b border-ez-text">
        <div className="max-w-[1600px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ez-border border border-ez-text">
            {[
              { i: Target, t: "Mission", d: "Close the execution gap in manufacturing supply chains with one accountable model." },
              { i: Broadcast, t: "Approach", d: "Run the work, instrument it with Eazymuv OS, and make the floor and the data agree." },
              { i: ArrowsIn, t: "Model", d: "Land inside one facility, prove execution, then expand across the network." },
            ].map((c, i) => (
              <StaggerItem key={i} className="bg-white p-10">
                <c.i size={30} weight="duotone" className="text-ez-yellow mb-6" />
                <div className="font-display text-xl uppercase tracking-tight">{c.t}</div>
                <p className="mt-3 text-sm text-ez-text/70">{c.d}</p>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.1}>
            <Link to="/team" data-testid="about-cta" className="mt-10 inline-flex items-center gap-2 bg-ez-text text-ez-bg px-7 py-4 font-semibold hover:bg-ez-yellow hover:text-ez-text transition-colors">
              Meet the Team <ArrowRight size={18} weight="bold" />
            </Link>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
